<script setup lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import { useAnalytics } from '@/composables/analytics';
import { useNewRelic } from '@/composables/useNewRelic';
import { useNotification } from '@/composables/useNotification';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import { Icons } from '@/models/icons.enum';
import { SelfSellInCatalogDownloadType } from '@onrunning/backstage-models';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ResolvedSelfSellInModel } from '../../composables/useSelfSellIn';
import InfoCardContainer from '../InfoCardContainer.vue';

const props = defineProps<{
  data: NonNullable<ResolvedSelfSellInModel['catalogDownloadSection']>['catalogs'][number];
  id: string;
}>();

const { t } = useI18n();
const { notifyWarning } = useNotification();
const newRelic = useNewRelic();
const { addCustomGaEvent } = useAnalytics();
const isDownloading = ref(false);

const titleParts = computed(() => props.data.title.split('/'));
const resolvedTitle = computed(() => titleParts.value[0]?.trim() ?? '');
const resolvedSubtitle = computed(() => titleParts.value[1]?.trim() ?? '');

const getIcon = computed(() => (isDownloading.value ? Icons.LOADING : Icons.DOWNLOAD_ARROW_SIGN));

const handleDownloadCatalog = (downloadUrl: string) => {
  window.open(downloadUrl, '_blank', 'noopener,noreferrer');
};

const handleGenerateCatalog = async () => {
  // TODO Placeholder for catalog generation logic (BACK-1833).
  isDownloading.value = true;
  try {
    await new Promise((resolve) => setTimeout(resolve, 1000));
  } catch (error) {
    newRelic.noticeError('Local error triggered. Error fetching the season catalog', {
      component: 'CatalogCard',
      method: 'handleGenerateCatalog',
      error,
    });
    notifyWarning(t('viewSelfSellIn.catalog_download.download_error', { fileName: resolvedTitle.value }));
  } finally {
    isDownloading.value = false;
  }
};

const handleClickAction = () => {
  if (props.data.type === SelfSellInCatalogDownloadType.OVERVIEW) {
    if (props.data.downloadUrl) {
      handleDownloadCatalog(props.data.downloadUrl);
    } else {
      notifyWarning(t('viewSelfSellIn.catalog_download.commercial_overview_url_missing'));
    }
  } else {
    handleGenerateCatalog();
  }

  // Analytics
  addCustomGaEvent({
    event: GaCustomEvents.FileDownload,
    link_text: `File download for ${props.data.type}`,
    file_name: resolvedTitle.value,
    component: 'CatalogCard',
  });
};
</script>

<template>
  <InfoCardContainer class="catalog-card" height="tall" :background-image-url="data.imageUrl" :id="id">
    <div class="card-header">
      <h3 class="card-header__title">{{ resolvedTitle }}</h3>
      <p class="card-header__subtitle">{{ resolvedSubtitle }}</p>
    </div>
    <BackstageButton
      class="download-button"
      @click="handleClickAction"
      :icon-right="getIcon"
      :disabled="isDownloading"
      data-cy-button="catalog-card-download-button"
      >{{ t('global.download_button') }}</BackstageButton
    >
  </InfoCardContainer>
</template>

<style lang="scss" scoped>
.catalog-card {
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.5) 100%);
    pointer-events: none;
  }
}
.card-header {
  z-index: 1;

  &__title,
  &__subtitle {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: -0.48px;
  }
}

.download-button {
  align-self: flex-end;
  z-index: 1;
}
</style>
